/* Writing a finished service up from its sale — the parts that need no board.
 *
 * Once a client is checked out, every booked service of theirs with no square
 * goes up on its own, finished, on the technician the SALE names. See "Writing
 * up the day as it is checked out" in store.js for when and why; this file is
 * the three pieces of it that can be asserted without a DOM or a database:
 *
 *   matchSaleLines   which sale line is which booked service. The sale knows
 *                    the appointment and the service, never the booked line,
 *                    so a visit with two of the same service is paired in
 *                    order — the first booked with the first rung up.
 *   writeUpWindow    when the square ran: the book's own window, cut short by
 *                    the checkout if the client paid before it was due to end.
 *   stableTurnId     the square's id, worked out rather than drawn at random,
 *                    so two stations writing up the same checkout write the
 *                    same row and the second write is a no-op instead of a
 *                    refused duplicate.
 *
 * Pure; this file imports nothing.
 */

/* `services` [{ id, serviceId, startsAt }] — one appointment's booked lines.
 * `lines`    [{ serviceId, staffId, label }] — that appointment's sale lines.
 * Returns { bookedLineId: saleLine } for every booked line a sale line was
 * found for. A booked line with no sale line of the same service is simply
 * missing from the answer: the service was changed or dropped at checkout, and
 * guessing which line took its place is the desk's call, not the board's. */
export function matchSaleLines(services, lines) {
  var pool = {};
  (lines || []).forEach(function (l) {
    var k = String(l.serviceId);
    (pool[k] = pool[k] || []).push(l);
  });
  var out = {};
  (services || []).slice()
    .sort(function (a, b) { return (a.startsAt || 0) - (b.startsAt || 0); })
    .forEach(function (s) {
      var left = pool[String(s.serviceId)];
      if (left && left.length) out[s.id] = left.shift();
    });
  return out;
}

/* The book's window for the service, never running past the moment the client
 * paid — a square cannot have been going after the ticket closed — and never
 * starting after it ends. Null when there is nothing to measure from. */
export function writeUpWindow(startsAt, bookedEnd, closedAt) {
  if (startsAt == null || bookedEnd == null) return null;
  var end = closedAt != null ? Math.min(bookedEnd, closedAt) : bookedEnd;
  return { startedAt: Math.min(startsAt, end), endedAt: end };
}

/* A UUID-shaped id that is the same every time for the same key. Not a
 * security property, only an agreement between stations: four FNV-1a passes
 * with different seeds give 128 bits, which is far more than one salon's day of
 * booked lines will ever collide in. The version nibble says 8 — RFC 9562's
 * "custom" — so nothing mistakes it for a random v4. */
export function stableTurnId(key) {
  var s = String(key);
  var seeds = [0x811c9dc5, 0x01000193, 0x9e3779b9, 0x85ebca6b];
  var hex = seeds.map(function (seed) {
    var h = seed >>> 0;
    for (var i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    // One more round over the seed itself, so the four passes differ even
    // where the input is short.
    h ^= seed;
    h = Math.imul(h, 0x01000193) >>> 0;
    return ('0000000' + h.toString(16)).slice(-8);
  }).join('');
  return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-8' + hex.slice(13, 16) + '-' +
    ((parseInt(hex.charAt(16), 16) & 0x3) | 0x8).toString(16) + hex.slice(17, 20) + '-' +
    hex.slice(20, 32);
}
